<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const {
  content,
  attachments,
  contentAttributes,
  additionalAttributes,
  messageType,
} = useMessageContext();

// ORACURA: a WhatsApp template sent with a header image / video / document
// (picked from a URL or uploaded from the device) carries it only in
// template_params - show it to the agent too, not just the template text.
const templateHeader = computed(() => {
  const attrs = additionalAttributes?.value || {};
  const params = attrs.templateParams || attrs.template_params || {};
  const processed = params.processedParams || params.processed_params || {};
  const header = processed.header || {};
  const url = header.mediaUrl || header.media_url;
  if (!url) return null;
  return {
    url,
    type: (header.mediaType || header.media_type || '').toLowerCase(),
    name:
      header.mediaName ||
      header.media_name ||
      decodeURIComponent(url.split('?')[0].split('/').pop() || 'file'),
  };
});

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const hasHeaderMedia = computed(() =>
  ['image', 'video'].includes(templateHeader.value?.type)
);

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble
    :class="hasHeaderMedia ? 'p-1.5 pb-3' : 'px-4 py-3'"
    data-bubble-name="text"
  >
    <div
      class="gap-3 flex flex-col"
      :class="hasHeaderMedia ? 'max-w-[300px]' : ''"
    >
      <template v-if="templateHeader">
        <a
          v-if="templateHeader.type === 'image'"
          :href="templateHeader.url"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            :src="templateHeader.url"
            :alt="templateHeader.name"
            loading="lazy"
            class="block w-full max-h-[320px] rounded-xl object-cover"
          />
        </a>
        <video
          v-else-if="templateHeader.type === 'video'"
          :src="templateHeader.url"
          controls
          preload="metadata"
          class="block w-full rounded-xl"
        />
        <a
          v-else
          :href="templateHeader.url"
          target="_blank"
          rel="noopener noreferrer"
          class="mx-2.5 mt-1.5 px-2 py-1 rounded-lg bg-n-alpha-3 truncate"
        >
          {{ templateHeader.name }}
        </a>
      </template>
      <span v-if="isEmpty && !templateHeader" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <FormattedContent
        v-if="renderContent"
        :content="renderContent"
        :class="hasHeaderMedia ? 'px-2.5' : ''"
      />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
